/**
 * Account + entitlement alert layer (APP-002 §15).
 *
 * Derives prominent-but-not-alarming alerts from backend overview data. Only
 * conditions supported by the overview/billing contract are surfaced. Each
 * alert includes a clear action and never exposes internal details. The layer
 * never locally decides whether an account retains paid access — it reads the
 * backend-provided signals (APP-002 §15).
 *
 * Alerts are ordered by severity. Unknown states degrade to a neutral notice,
 * never to an alarming one.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AlertTriangle, ShieldAlert } from '@/components/icons';
import type { OverviewResponse } from '@/api/dashboard-types';
import { isUnlimited, usagePercent } from '@/lib/format';

export interface AccountAlert {
  id: string;
  variant: 'warning' | 'destructive' | 'info';
  title: string;
  description: string;
  action?: ReactNode;
}

const QUOTA_WARNING_THRESHOLD = 0.8; // 80% used

/**
 * Derive the set of active account/entitlement alerts from the overview. Only
 * data the contract actually returns is used; missing fields produce no alert
 * (never a fabricated one).
 */
export function deriveAccountAlerts(overview: OverviewResponse): AccountAlert[] {
  const alerts: AccountAlert[] = [];
  const { billing, usage, plan } = overview;

  // Past-due subscription — financial state. Surface as a warning; the billing
  // surface (placeholder in APP-002) is the action.
  if (billing.subscription_status === 'past_due' || billing.subscription_status === 'unpaid') {
    alerts.push({
      id: 'subscription-past-due',
      variant: billing.subscription_status === 'unpaid' ? 'destructive' : 'warning',
      title: 'Payment requires attention',
      description:
        'Your subscription payment is past due. Access may be reduced if this is not resolved.',
      action: (
        <Button size="sm" variant="outline" asChild>
          <Link to="/billing">Review billing</Link>
        </Button>
      ),
    });
  }

  // Entitlement status — read the backend signal, do not infer from Stripe.
  const ent = (plan.entitlement_status ?? '').toLowerCase();
  if (ent === 'lapsed' || ent === 'expired') {
    alerts.push({
      id: 'access-lapsed',
      variant: 'destructive',
      title: 'Plan access has lapsed',
      description: 'Your paid plan access has lapsed. Restore access from billing.',
      action: (
        <Button size="sm" variant="outline" asChild>
          <Link to="/billing">Restore access</Link>
        </Button>
      ),
    });
  } else if (ent === 'grace' || ent === 'grace_period') {
    alerts.push({
      id: 'access-grace',
      variant: 'warning',
      title: 'Access in grace period',
      description: 'Your plan is in a grace period. Update billing to retain uninterrupted access.',
      action: (
        <Button size="sm" variant="outline" asChild>
          <Link to="/billing">Update billing</Link>
        </Button>
      ),
    });
  }

  // Quota nearly exhausted / exhausted — read authoritative numbers only.
  if (!isUnlimited(usage.requests_limit)) {
    const { raw } = usagePercent(usage.requests_used, usage.requests_limit);
    if (raw >= 100) {
      alerts.push({
        id: 'quota-exhausted',
        variant: 'destructive',
        title: 'Monthly request quota reached',
        description: 'You have used your monthly request allowance. Requests will be limited until the window resets.',
        action: (
          <Button size="sm" variant="outline" asChild>
            <Link to="/billing">Upgrade plan</Link>
          </Button>
        ),
      });
    } else if (raw >= QUOTA_WARNING_THRESHOLD * 100) {
      alerts.push({
        id: 'quota-warning',
        variant: 'warning',
        title: 'Approaching request quota',
        description: 'You have used most of your monthly request allowance.',
      });
    }
  }

  return alerts;
}

/**
 * Render the active alerts in severity order. Renders nothing when there are
 * no alerts (prominent only when there is cause — APP-002 §15).
 */
export function AccountAlerts({ alerts }: { alerts: AccountAlert[] }) {
  if (alerts.length === 0) return null;
  return (
    <div className="space-y-3" data-testid="account-alerts">
      {alerts.map((alert) => {
        const Icon = alert.variant === 'destructive' ? ShieldAlert : AlertTriangle;
        return (
          <Alert key={alert.id} variant={alert.variant}>
            <Icon className="h-4 w-4" />
            <AlertTitle>{alert.title}</AlertTitle>
            <AlertDescription>
              <p>{alert.description}</p>
              {alert.action ? <div className="mt-3">{alert.action}</div> : null}
            </AlertDescription>
          </Alert>
        );
      })}
    </div>
  );
}
